import { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { readableColor } from 'polished';
import { themeGet } from '@styled-system/theme-get';
import { useLocation } from '@reach/router';

import { fixPropWarnings } from '../utils/fixPropWarnings';

import SectionContainer from './SectionContainer';
import InlineLink from './InlineLink';

const Section = styled.section`
  background-color: ${({ theme, $background }) => theme.colors[$background]};
  color: ${({ theme, $background }) => readableColor(
    theme.colors[$background],
    theme.colors.black,
    theme.colors.white,
  )};
  padding: 30px 0;

  ${({ $variant }) => $variant === 'top-links'
    && `
      padding: 20px 0;
    `}

  @media only screen and (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    padding: 50px 0 60px;

    ${({ $variant }) => $variant === 'top-links'
    && `
      padding: 20px 0;
    `}
  }

  ${({ $sticky }) => $sticky && css`
    @media only screen and (min-width: ${({ theme }) => theme.breakpoints.md}) {
      position: sticky;
      top: 70px;
      z-index: 2;
    }
  `}
`;

const Grid = styled(SectionContainer).withConfig(fixPropWarnings('variant'))`
  margin-top: 0;
  margin-bottom: 0;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;

  @media only screen and (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    ${({ variant }) => variant === 'top-links'
    && `
        &:lang(en) {
          display: grid;
          grid-template-rows: 1fr 1fr;
          grid-template-columns: auto auto auto;
          grid-gap: 10px clamp(18px, 5vw, 1000px);
        }
      `}
  }

  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    align-items: flex-start;
    width: fit-content;

    ${({ variant }) => variant === 'top-links'
    && `
        display: flex;
        flex-direction: row;
        justify-content: space-evenly;
      `}
    ${({ variant }) => variant === 'province-links'
    && css`
        align-items: center;
      `}
  }

  @media only screen and (min-width: ${({ theme }) => theme.breakpoints.md}) {
    ${({ variant }) => variant === 'top-links'
    && `
        &:lang(en) {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-wrap: wrap;
          grid-gap: unset;
        }
      `}
  }
`;

const ProvinceJumpLinkTitle = styled.h2`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${themeGet('typography.mobile.heading2.fontWeight')};
  font-size: ${themeGet('typography.mobile.heading2.fontSize')};
  line-height: ${themeGet('typography.mobile.heading2.lineHeight')};
  letter-spacing: ${themeGet('typography.mobile.heading2.letterSpacing')};
  text-align: center;
  margin: 0 0 30px 0;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    font-size: ${themeGet('typography.desktop.heading2.fontSize')};
    line-height: ${themeGet('typography.desktop.heading2.lineHeight')};
    margin: 0 0 40px 0;
  }
`;

const JumpLink = styled(InlineLink).withConfig(fixPropWarnings('variant'))`
  display: flex;
  align-items: center;
  margin: 15px 12.5px;
  font-size: 14px;
  line-height: 18px;
  font-weight: 500;
  color: inherit;
  text-align: center;

  ${({ variant }) => variant === 'top-links'
    && `
      margin: 10px 12.5px;
    `}

  &::before {
    ${({ icon }) => icon && `content: ''`};
    height: 45px;
    width: 45px;
    background-image: url(${({ icon }) => icon});
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    margin-right: 12px;
  }
  ${({ variant }) => variant === 'province-links'
    && css`
      color: ${themeGet('colors.velvet')};
      font-size: 24px;
      font-weight: 500;
    `}

  @media only screen and (max-width: 500px) {
    &:nth-of-type(1n + 4) {
      ${({ variant }) => variant !== 'top-links'
    && variant !== 'province-links'
    && `
        display: none;
      `}
    }
  }

  @media only screen and (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    ${({ variant }) => variant === 'top-links'
    && `
        &:lang(en) {
          margin: 0;
          justify-content: center;
        }
      `}

    ${({ variant }) => variant === 'province-links'
    && css`
        font-size: 16px;
        font-weight: 500;
        line-height: 24px;
      `}
  }

  @media only screen and (min-width: ${({ theme }) => theme.breakpoints.md}) {
    ${({ variant }) => variant === 'top-links'
    && `
        margin: 0px 10px;

        &:lang(en) {
          margin: 0px 10px;
        }
      `}

    &::before {
      height: 36px;
      width: 36px;
    }

    &:first-child {
      margin-left: 0;
    }

    &:last-child {
      margin-right: 0;
    }
  }

  @media only screen and (min-width: 768px) {
    &::before {
      height: 40px;
      width: 40px;
    }
  }

  @media only screen and (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    font-size: 18px;
    line-height: 24px;
    font-weight: 600;

    ${({ variant }) => variant === 'top-links'
    && `
        margin: 0px 12.5px;

        &:lang(en) {
          margin: 0px 12.5px;
        }
      `}

    ${({ variant }) => variant === 'province-links'
    && css`
        font-size: 24px;
        font-weight: 500;
        line-height: 36px;
      `}
  }

  @media only screen and (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    margin: 0 20px;

    &::before {
      height: 45px;
      width: 45px;
    }
  }
`;

const JumpLinks = ({ className, data, sticky, variant }) => {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash && typeof window !== 'undefined') window.scrollTo(0, 0);
  }, [hash]);

  if (!data) return null;

  const {
    backgroundColor,
    combinatorSectionItems,
    links,
    sectionHeading,
    sectionId,
  } = data;
  
  const items = combinatorSectionItems || links;

  return (
    <Section
      id={sectionId || 'jump-links'}
      className={className}
      $background={backgroundColor}
      $variant={variant}
      $sticky={sticky}
    >
      {sectionHeading && (
        <ProvinceJumpLinkTitle>{sectionHeading}</ProvinceJumpLinkTitle>
      )}
      <Grid variant={variant}>
        {items
          && items.map(({ id, linkText, linkUrl, iconDamAsset }) => (
            <JumpLink
              key={id}
              href={linkUrl}
              icon={
                  iconDamAsset
                    ? iconDamAsset?.asset.localImage.publicURL
                    : null
                }
              variant={variant}
            >
              {linkText}
            </JumpLink>
          ))}
      </Grid>
    </Section>
  );
};

JumpLinks.propTypes = {
  className: PropTypes.string,
  data: PropTypes.shape().isRequired,
  sticky: PropTypes.bool,
  variant: PropTypes.string,
};

JumpLinks.defaultProps = {
  className: null,
  sticky: false,
  variant: null,
};

export default JumpLinks;
